import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Loader2, Pause, Play, Users, X } from 'lucide-react';
import {
  CandidatoVoce, elencaAudio, erroreRichiesta, leggiVoci, RiepilogoAudio, RiepilogoVoce, salvaVoci, urlAssolutoAudio,
  VoceDaSalvare, VoceVerbale,
} from '../../services/verbaliAudioService';
import { useToast } from '../../contexts/ToastContext';
import { formattaMs } from '../../utils/verbaliTrascrizione';
import { nomeAudio, TESTO_SENZA_VOCI, TESTO_VOCI_FACOLTATIVE } from './testiAudio';

interface VerbaleVociProps {
  eventId: string;
  aperto: boolean;
  onChiudi: () => void;
  /** Dopo il salvataggio (la finestra si chiude subito dopo). */
  onSalvato: (voci: Array<VoceVerbale | VoceDaSalvare>) => void;
}

/** Lunghezza massima del nome di una voce (come il server). */
export const MAX_NOME_VOCE = 160;
/** Ascolto di un esempio: si ferma da solo dopo questo tempo. */
const DURATA_ESEMPIO_MS = 12000;

const RE_LOCALE = /^T(\d+)·(.+)$/;

/** `T3·B` ⇒ «Tratto 3 · Voce B»; le voci certe restano come sono («Voce 2»). */
export function etichettaLeggibile(etichetta: string): string {
  const m = RE_LOCALE.exec(etichetta || '');
  return m ? `Tratto ${m[1]} · Voce ${m[2]}` : etichetta;
}

type Modo = '' | 'candidato' | 'uguale' | 'libero';
interface Bozza {
  modo: Modo;
  candidato: number;
  uguale: string;
  nome: string;
}

const chiaveVoce = (v: { audioId: string; etichetta: string }) => `${v.audioId}|${v.etichetta}`;

function bozzaIniziale(v: RiepilogoVoce, candidati: CandidatoVoce[]): Bozza {
  if (v.uguale && !v.certa) return { modo: 'uguale', candidato: -1, uguale: v.uguale, nome: '' };
  const nome = (v.nome || '').trim();
  if (!nome) return { modo: '', candidato: -1, uguale: '', nome: '' };
  const i = candidati.findIndex((c) => c.nome === nome && (c.invitationId || null) === (v.invitationId || null));
  if (i >= 0) return { modo: 'candidato', candidato: i, uguale: '', nome: '' };
  return { modo: 'libero', candidato: -1, uguale: '', nome };
}

const valoreScelta = (b: Bozza) => {
  if (b.modo === 'candidato') return `c:${b.candidato}`;
  if (b.modo === 'uguale') return `u:${b.uguale}`;
  return b.modo;
};

const minuti = (n: number) => `${String(Math.round((n || 0) * 10) / 10).replace('.', ',')} min`;

/** Nome scelto in bozza (o null): serve a «Stessa persona di Voce 2 · Mario Bianchi». */
function nomeInBozza(b: Bozza | undefined, candidati: CandidatoVoce[]): string | null {
  if (!b) return null;
  if (b.modo === 'candidato') return candidati[b.candidato]?.nome || null;
  if (b.modo === 'libero') return b.nome.trim() || null;
  return null;
}

/** Finestra delle voci: a chi corrisponde ogni voce della trascrizione (facoltativo). */
const VerbaleVoci: React.FC<VerbaleVociProps> = ({ eventId, aperto, onChiudi, onSalvato }) => {
  const toast = useToast();
  const [caricamento, setCaricamento] = useState(false);
  const [erroreCaricamento, setErroreCaricamento] = useState<string | null>(null);
  const [voci, setVoci] = useState<RiepilogoVoce[]>([]);
  const [candidati, setCandidati] = useState<CandidatoVoce[]>([]);
  const [audio, setAudio] = useState<RiepilogoAudio[]>([]);
  const [bozze, setBozze] = useState<Record<string, Bozza>>({});
  const [salvando, setSalvando] = useState(false);
  const [erroreSalvataggio, setErroreSalvataggio] = useState<string | null>(null);
  const [inAscolto, setInAscolto] = useState<string | null>(null);

  const lettoreRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Esempio chiesto: il conto dei 12 s parte quando l'audio suona davvero (`playing`), non al clic. */
  const inAscoltoRef = useRef<string | null>(null);

  const fermaAscolto = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    inAscoltoRef.current = null;
    try { lettoreRef.current?.pause(); } catch { /* già fermo */ }
    setInAscolto(null);
  }, []);

  const alSuonare = useCallback(() => {
    if (inAscoltoRef.current && !timerRef.current) timerRef.current = setTimeout(fermaAscolto, DURATA_ESEMPIO_MS);
  }, [fermaAscolto]);

  useEffect(() => {
    if (!aperto) return undefined;
    let vivo = true;
    setCaricamento(true);
    setErroreCaricamento(null);
    setErroreSalvataggio(null);
    Promise.all([leggiVoci(eventId), elencaAudio(eventId).catch(() => null)])
      .then(([v, dati]) => {
        if (!vivo) return;
        setVoci(v.voci);
        setCandidati(v.candidati);
        setAudio(dati?.audio || []);
        const iniziali: Record<string, Bozza> = {};
        v.voci.forEach((voce) => { iniziali[chiaveVoce(voce)] = bozzaIniziale(voce, v.candidati); });
        setBozze(iniziali);
      })
      .catch((err) => { if (vivo) setErroreCaricamento(erroreRichiesta(err).messaggio); })
      .finally(() => { if (vivo) setCaricamento(false); });
    return () => {
      vivo = false;
      fermaAscolto();
    };
  }, [aperto, eventId, fermaAscolto]);

  useEffect(() => {
    if (!aperto) return undefined;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !salvando) onChiudi(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [aperto, salvando, onChiudi]);

  /** Voci raggruppate per audio, nell'ordine degli audio; certe prima, poi le incerte. */
  const gruppi = useMemo(() => {
    const ordine = new Map(audio.map((a) => [a._id, a]));
    const perAudio = new Map<string, RiepilogoVoce[]>();
    voci.forEach((v) => perAudio.set(v.audioId, [...(perAudio.get(v.audioId) || []), v]));
    // Audio trascritti senza voci (diarizzazione non riuscita): si dice, senza righe.
    audio.filter((a) => a.stato === 'trascritta' && a.senzaVoci && !perAudio.has(a._id)).forEach((a) => perAudio.set(a._id, []));
    return Array.from(perAudio.entries())
      .map(([audioId, elenco]) => ({
        audioId,
        info: ordine.get(audioId) || null,
        certe: elenco.filter((v) => v.certa),
        incerte: elenco.filter((v) => !v.certa),
      }))
      .sort((x, y) => (x.info?.ordine ?? 999) - (y.info?.ordine ?? 999));
  }, [voci, audio]);

  const ascolta = (audioId: string, daMs: number, chiave: string) => {
    const el = lettoreRef.current;
    const info = audio.find((a) => a._id === audioId);
    const url = info && !info.eliminatoAt ? urlAssolutoAudio(info.urlAscolto) : null;
    if (!el || !url) return;
    if (inAscolto === chiave) {
      fermaAscolto();
      return;
    }
    fermaAscolto();
    try {
      if (el.getAttribute('src') !== url) el.src = url;
      el.currentTime = Math.max(0, daMs) / 1000;
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => { if (inAscoltoRef.current === chiave) fermaAscolto(); });
      inAscoltoRef.current = chiave;
      setInAscolto(chiave);
    } catch {
      fermaAscolto();
    }
  };

  const cambiaScelta = (chiave: string, valore: string) => {
    setBozze((b) => {
      const prima = b[chiave] || { modo: '', candidato: -1, uguale: '', nome: '' };
      if (valore.startsWith('c:')) return { ...b, [chiave]: { ...prima, modo: 'candidato', candidato: Number(valore.slice(2)), uguale: '' } };
      if (valore.startsWith('u:')) return { ...b, [chiave]: { ...prima, modo: 'uguale', uguale: valore.slice(2), candidato: -1 } };
      if (valore === 'libero') return { ...b, [chiave]: { ...prima, modo: 'libero', candidato: -1, uguale: '' } };
      return { ...b, [chiave]: { modo: '', candidato: -1, uguale: '', nome: '' } };
    });
  };

  const salva = async () => {
    const elenco: VoceDaSalvare[] = [];
    voci.forEach((v) => {
      const b = bozze[chiaveVoce(v)];
      if (!b) return;
      if (b.modo === 'candidato' && candidati[b.candidato]) {
        const c = candidati[b.candidato];
        elenco.push({ audioId: v.audioId, etichetta: v.etichetta, nome: c.nome, invitationId: c.invitationId ?? null });
      } else if (b.modo === 'libero' && b.nome.trim()) {
        elenco.push({ audioId: v.audioId, etichetta: v.etichetta, nome: b.nome.trim().slice(0, MAX_NOME_VOCE), invitationId: null });
      } else if (b.modo === 'uguale' && b.uguale) {
        elenco.push({ audioId: v.audioId, etichetta: v.etichetta, uguale: b.uguale });
      }
    });
    setSalvando(true);
    setErroreSalvataggio(null);
    try {
      const salvate = await salvaVoci(eventId, elenco);
      fermaAscolto();
      toast.success('Voci salvate');
      onSalvato(salvate);
      onChiudi();
    } catch (err) {
      setErroreSalvataggio(erroreRichiesta(err).messaggio);
    } finally {
      setSalvando(false);
    }
  };

  if (!aperto) return null;

  const rigaVoce = (v: RiepilogoVoce, certeDelloStesso: RiepilogoVoce[]) => {
    const chiave = chiaveVoce(v);
    const b = bozze[chiave] || { modo: '', candidato: -1, uguale: '', nome: '' };
    const etichetta = etichettaLeggibile(v.etichetta);
    const info = audio.find((a) => a._id === v.audioId);
    const ascoltabile = !!(info && !info.eliminatoAt && info.urlAscolto);
    const assegnata = nomeInBozza(b, candidati)
      || (b.modo === 'uguale' && b.uguale ? (nomeInBozza(bozze[`${v.audioId}|${b.uguale}`], candidati) || b.uguale) : null);
    return (
      <li key={chiave} className="rounded-xl border border-slate-200 p-3 dark:border-gray-700">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-sm font-semibold text-slate-900 dark:text-white">{etichetta}</span>
          <span className={`text-sm ${assegnata ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-400 dark:text-gray-500'}`}>
            {`→ ${assegnata || '?'}`}
          </span>
          <span className="ml-auto text-xs text-slate-500 dark:text-gray-400">{minuti(v.minuti)}</span>
        </div>

        {v.esempi.length > 0 && (
          <ul className="mt-2 space-y-1">
            {v.esempi.map((e) => {
              const chiaveEsempio = `${chiave}|${e.daMs}`;
              const suona = inAscolto === chiaveEsempio;
              return (
                <li key={chiaveEsempio} className="flex items-start gap-2 text-xs text-slate-600 dark:text-gray-300">
                  <button
                    type="button"
                    onClick={() => ascolta(v.audioId, e.daMs, chiaveEsempio)}
                    disabled={!ascoltabile}
                    aria-label={suona ? 'Ferma l\'ascolto' : `Ascolta da ${formattaMs(e.daMs)}`}
                    className="inline-flex flex-shrink-0 items-center gap-1 rounded-md bg-emerald-50 px-1.5 py-0.5 font-semibold text-emerald-700 hover:bg-emerald-100 disabled:bg-slate-100 disabled:text-slate-400 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50 dark:disabled:bg-gray-700 dark:disabled:text-gray-500"
                  >
                    {suona ? <Pause className="h-3 w-3" aria-hidden="true" /> : <Play className="h-3 w-3" aria-hidden="true" />}
                    <span className="tabular-nums">{formattaMs(e.daMs)}</span>
                  </button>
                  <span className="line-clamp-2">«{e.testo}»</span>
                </li>
              );
            })}
          </ul>
        )}

        <div className="mt-2.5 flex flex-col gap-2 sm:flex-row">
          <select
            value={valoreScelta(b)}
            onChange={(e) => cambiaScelta(chiave, e.target.value)}
            aria-label={`Chi è ${etichetta}`}
            className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          >
            <option value="">Non associata</option>
            {candidati.length > 0 && (
              <optgroup label="Presenti e invitati">
                {candidati.map((c, i) => (
                  <option key={`${c.nome}-${i}`} value={`c:${i}`}>
                    {c.dettaglio ? `${c.nome} · ${c.dettaglio}` : c.nome}
                  </option>
                ))}
              </optgroup>
            )}
            {!v.certa && certeDelloStesso.length > 0 && (
              <optgroup label="Voce già riconosciuta">
                {certeDelloStesso.map((c) => {
                  const nome = nomeInBozza(bozze[chiaveVoce(c)], candidati);
                  return (
                    <option key={c.etichetta} value={`u:${c.etichetta}`}>
                      {`Stessa persona di ${c.etichetta}${nome ? ` · ${nome}` : ''}`}
                    </option>
                  );
                })}
              </optgroup>
            )}
            <option value="libero">Altro nome…</option>
          </select>
          {b.modo === 'libero' && (
            <input
              type="text"
              value={b.nome}
              onChange={(e) => {
                const nome = e.target.value;
                setBozze((s) => ({ ...s, [chiave]: { ...b, nome } }));
              }}
              maxLength={MAX_NOME_VOCE}
              placeholder="Nome e cognome"
              aria-label={`Nome di ${etichetta}`}
              autoFocus
              className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            />
          )}
        </div>
      </li>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !salvando) onChiudi(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="verbale-voci-titolo"
        className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl dark:bg-gray-800"
      >
        <div className="flex items-start gap-3 border-b border-slate-200 px-5 py-4 dark:border-gray-700">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
            <Users className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h3 id="verbale-voci-titolo" className="text-base font-semibold text-slate-900 dark:text-white">Chi parla nella registrazione</h3>
            <p className="text-xs text-slate-500 dark:text-gray-400">{TESTO_VOCI_FACOLTATIVE}</p>
          </div>
          <button
            type="button"
            onClick={onChiudi}
            disabled={salvando}
            aria-label="Chiudi"
            className="ml-auto rounded p-1 text-slate-400 hover:text-slate-700 disabled:opacity-50 dark:hover:text-gray-200"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          {caricamento && (
            <p className="flex items-center gap-2 text-sm text-slate-500 dark:text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Caricamento delle voci…
            </p>
          )}
          {erroreCaricamento && <p className="text-sm text-red-600 dark:text-red-400">{erroreCaricamento}</p>}
          {!caricamento && !erroreCaricamento && gruppi.length === 0 && (
            <p className="text-sm text-slate-500 dark:text-gray-400">Non ci sono voci da associare.</p>
          )}
          {!caricamento && gruppi.map((g) => (
            <section key={g.audioId} aria-label={g.info ? nomeAudio(g.info) : 'Audio'}>
              {gruppi.length > 1 && (
                <h4 className="mb-2 text-sm font-semibold text-slate-700 dark:text-gray-200">
                  {g.info ? nomeAudio(g.info) : 'Audio'}
                </h4>
              )}
              {g.certe.length === 0 && g.incerte.length === 0 && (
                <p className="text-sm text-slate-500 dark:text-gray-400">{TESTO_SENZA_VOCI}.</p>
              )}
              {g.certe.length > 0 && <ul className="space-y-2">{g.certe.map((v) => rigaVoce(v, g.certe))}</ul>}
              {g.incerte.length > 0 && (
                <div className="mt-4">
                  <p className="mb-2 flex items-start gap-1.5 text-xs text-amber-800 dark:text-amber-200">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                    In questi tratti il riconoscimento non è sicuro: indica chi parla, oppure se è la stessa persona di una voce già riconosciuta.
                  </p>
                  <ul className="space-y-2">{g.incerte.map((v) => rigaVoce(v, g.certe))}</ul>
                </div>
              )}
            </section>
          ))}
        </div>

        {erroreSalvataggio && (
          <p className="mx-5 mb-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300" role="alert">
            {erroreSalvataggio}
          </p>
        )}
        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3 dark:border-gray-700">
          <button
            type="button"
            onClick={onChiudi}
            disabled={salvando}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={salva}
            disabled={salvando || caricamento || !!erroreCaricamento || voci.length === 0}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {salvando && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
            Salva
          </button>
        </div>
        {/* Lettore condiviso degli esempi (nascosto). */}
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio ref={lettoreRef} preload="none" className="hidden" onPlaying={alSuonare} onEnded={fermaAscolto} />
      </div>
    </div>
  );
};

export default VerbaleVoci;
